import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Collapse,
  Divider,
  Flex,
  IconButton,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  AlertDescription,
  Text,
} from '@chakra-ui/react';
import { pxToRem, gradeBenchPanelActions, useHandleRubricAnswerChange, spanSelectors } from '@codametrix/ui-common';
import {
  getWorkflowForCodeType,
  codeTypeToEntityType,
  getQuestionIdsToClear,
} from '../highlight-drawer/rubric-utils';
import { useRubricAutofill } from '../highlight-drawer/useRubricAutofill';
import {
  useWorkflowEvaluation,
  EvaluateWorkflowFn,
} from '../highlight-drawer/useWorkflowEvaluation';
import WorkflowWizard, { buildAllAnswerSummaries } from './workflow-wizard';
import './styles/index.scss';

export interface CaseQuestionsFormProps {
  // The workitem ID (t_work_item.id) used as entityId for CASE attributes
  sdWorkitemId: string;
  // Function to call the workflow evaluation API
  evaluateFn?: EvaluateWorkflowFn;
  // Work item ID (t_work_item.id) for system question expression evaluation
  workItemId?: number;
  // Optional disabled state
  isDisabled?: boolean;
}

/**
 * CaseQuestionsForm displays case-level questions from the rubric's CASE workflow.
 * Uses a wizard-style one-at-a-time layout : answered questions appear
 * as compact summaries; only the active question is interactive.
 * Auto-collapses when all required questions are answered.
 */
export const CaseQuestionsForm: React.FC<CaseQuestionsFormProps> = ({
  sdWorkitemId,
  evaluateFn,
  workItemId,
  isDisabled = false
}) => {
  const dispatch = useDispatch();

  const rubricVersion = useSelector(
    (state: any) => state.gradeBenchPanel?.rubricVersion
  ) as CQA.FullRubricVersion | null | undefined;

  const caseAttributes = useSelector(
    (state: any) => state.gradeBenchPanel?.caseAttributes
  ) as CQA.Attribute[] | undefined;

  const attributesFetched = useSelector(
    (state: any) => state.gradeBenchPanel?.attributesFetched
  ) as boolean | undefined;

  // [CDMX-17055] set when the attribute load (case GET / batch POST) failed; without this the
  // !attributesFetched spinner below would hang forever on a failed load.
  const attributesFetchError = useSelector(
    (state: any) => state.gradeBenchPanel?.attributesFetchError
  ) as boolean | undefined;

  const attributes = caseAttributes || [];
  const allSpans = useSelector(
    (state: any) => spanSelectors.selectSpanAnnotations(state.gradeBenchPanel)
  ) as Accelerate.SpanAnnotation[];
  const isAttributesLoading = !attributesFetched;

  const caseWorkflow = React.useMemo(
    () => getWorkflowForCodeType(rubricVersion, 'case'),
    [rubricVersion]
  );

  const {
    evaluation,
    visibleQuestions,
    filteredAnswersMap,
    autofilledAnswersMap,
    caseLevelComplete,
    isLoading: isEvaluationLoading,
    error,
  } = useWorkflowEvaluation({
    workflowId: caseWorkflow?.id,
    attributes,
    workflowQuestions: caseWorkflow?.questions,
    workItemId,
    evaluateFn: evaluateFn!,
    enabled: !!evaluateFn && !!caseWorkflow?.id,
  });

  useEffect(() => {
    dispatch(gradeBenchPanelActions.setCaseLevelComplete(caseLevelComplete));
  }, [caseLevelComplete, dispatch]);

  // Collapse when complete; user can re-expand to review/reset.
  const [isOpen, setIsOpen] = useState(true);
  useEffect(() => {
    if (caseLevelComplete === true) setIsOpen(false);
  }, [caseLevelComplete]);

  const lastAnswerSummary = useMemo(
    () => buildAllAnswerSummaries(visibleQuestions, attributes).at(-1) ?? null,
    [visibleQuestions, attributes]
  );

  const hasAnsweredCaseQuestions = lastAnswerSummary !== null;
  const caseWizardResetRef = useRef<(() => void) | null>(null);

  const [lastChangedQuestionId, setLastChangedQuestionId] = useState<number | null>(null);

  const handleAttributeChange = useCallback(
    (params: CQA.UpdateAttributeParams) => {
      dispatch(gradeBenchPanelActions.updateAttribute(params) as any);
    },
    [dispatch]
  );

  const handleDeleteAttribute = useCallback(
    async (params: CQA.DeleteAttributeParams) => {
      await dispatch(gradeBenchPanelActions.deleteAttribute(params) as any);
    },
    [dispatch]
  );

  const handleClearDownstream = useCallback(
    (params: CQA.ClearDownstreamAttributesParams) => {
      dispatch(gradeBenchPanelActions.clearDownstreamAttributes(params));
    },
    [dispatch]
  );

  const handleAnswerChangeWithCleanup = useHandleRubricAnswerChange(
    handleAttributeChange,
    handleDeleteAttribute,
    handleClearDownstream
  );

  const handleAnswerChangeRef = useRef<(questionId: number, value: number | number[] | string) => void>(() => {});

  const { trackAnswerChange, clearDownstreamTracking } = useRubricAutofill({
    entityId: sdWorkitemId,
    entityKey: `case-${sdWorkitemId}`,
    attributes,
    autofilledAnswers: autofilledAnswersMap,
    onAutofill: (questionId, answerValueId) => {
      handleAnswerChangeRef.current(questionId, answerValueId);
    }
  });

  const handleAnswerChange = useCallback(
    async (questionId: number, value: number | number[] | string) => {
      if (!caseWorkflow?.questions) return;

      setLastChangedQuestionId(questionId);
      // Re-open if answering while collapsed
      setIsOpen(true);

      const downstreamQuestionIds = getQuestionIdsToClear(
        visibleQuestions,
        attributes,
        questionId
      );

      const isFreeText = typeof value === 'string';
      if (!isFreeText && typeof value === 'number') {
        trackAnswerChange(questionId, value);
      }
      clearDownstreamTracking(downstreamQuestionIds);

      const question = visibleQuestions.find(wq => wq.question.id === questionId)?.question;
      const isSpan = question?.questionType === 'SPAN';
      const isSpans = question?.questionType === 'SPANS';

      let answerFields: Record<string, unknown>;
      if (isFreeText) {
        answerFields = { answerDisplayText: value };
      } else if (isSpan || isSpans) {
        answerFields = { spanAnnotationIds: value };
      } else {
        answerFields = { answerValueId: value };
      }

      await handleAnswerChangeWithCleanup({
        entityId: sdWorkitemId,
        entityType: codeTypeToEntityType('case'),
        attributes,
        questionId,
        ...answerFields,
        downstreamQuestionIds
      });
    },
    [caseWorkflow, attributes, visibleQuestions, sdWorkitemId, handleAnswerChangeWithCleanup, trackAnswerChange, clearDownstreamTracking]
  );

  handleAnswerChangeRef.current = handleAnswerChange;

  if (!rubricVersion) return null;
  if (!caseWorkflow || !caseWorkflow.questions || caseWorkflow.questions.length === 0) return null;
  const hasValidQuestions = caseWorkflow.questions.some(
    wq => (wq.question.answers && wq.question.answers.length > 0) ||
           wq.question.questionType === 'SPAN' || wq.question.questionType === 'SPANS'
  );
  if (!hasValidQuestions) return null;

  const boxProps = {
    className: 'case-questions-form',
    p: 5,
    bg: '#FEEEEA',
    borderRadius: 'md',
    border: '1px solid',
    borderColor: '#c37c6a',
  } as const;

  if (isAttributesLoading && !attributesFetchError) {
    return (
      <Box {...boxProps}>
        <Text fontSize={pxToRem(14)} fontWeight="semibold" color="gray.700" mb={1}>
          Case Questions
        </Text>
        <Divider mb={3} />
        <Center py={4}>
          <Spinner size="md" color="#c37c6a" />
        </Center>
      </Box>
    );
  }

  return (
    <Box {...boxProps}>
      {/* Header row — always visible; shows summary when collapsed */}
      <Flex align="center" justify="space-between" mb={isOpen ? 0 : 0} cursor="pointer" onClick={() => setIsOpen(o => !o)}>
        <Box flex={1} minWidth={0}>
          <Text fontSize={pxToRem(14)} fontWeight="semibold" color="gray.700" display="inline">
            Case Questions
          </Text>
          {!isOpen && lastAnswerSummary && (
            <Text as="span" fontSize="xs" color="gray.600" ml={2}>
              <Text as="span" fontWeight="semibold">{lastAnswerSummary.label}</Text>
              <Text as="span" mx="3px" color="gray.400">—</Text>
              {lastAnswerSummary.answer}
            </Text>
          )}
        </Box>
        <IconButton
          aria-label={hasAnsweredCaseQuestions ? 'Start over' : (isOpen ? 'Collapse case questions' : 'Expand case questions')}
          icon={
            <Text fontSize="xs" lineHeight={1}>
              {hasAnsweredCaseQuestions ? '↩' : (isOpen ? '▲' : '▼')}
            </Text>
          }
          variant="ghost"
          size="xs"
          onClick={e => {
            e.stopPropagation();
            if (hasAnsweredCaseQuestions) {
              caseWizardResetRef.current?.();
              setIsOpen(true);
            } else {
              setIsOpen(o => !o);
            }
          }}
        />
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <Divider mt={3} mb={3} />
        {(error || attributesFetchError) ? (
          <Alert status="error" variant="left-accent" borderRadius="md">
            <AlertIcon />
            <AlertDescription fontSize="sm">
              Unable to load case questions. Please try refreshing the page.
            </AlertDescription>
          </Alert>
        ) : isEvaluationLoading && !evaluation ? (
          <Center py={4}>
            <Spinner size="md" color="#c37c6a" />
          </Center>
        ) : (
          <WorkflowWizard
            visibleQuestions={visibleQuestions}
            attributes={attributes}
            onAnswerChange={handleAnswerChange}
            isDisabled={isDisabled}
            isLoading={isEvaluationLoading}
            autofilledAnswersMap={autofilledAnswersMap}
            filteredAnswersMap={filteredAnswersMap}
            spans={allSpans}
            lastChangedQuestionId={lastChangedQuestionId}
            resetRef={caseWizardResetRef}
          />
        )}
      </Collapse>
    </Box>
  );
};

export default CaseQuestionsForm;
